import React from 'react';
import {Column, Line} from '@ant-design/plots';
import {Card, Row, Col, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {NoDataPlaceholder, truncateStr} from '../../../utils/helpers';

type EarningsAndCLicksProps = {
    budgetUtilizationPerCampaign: any,
    clicksPerCampaign: any,
    clicksGeographicalDistribution: any
};

interface DataItem {
    campaign_name: string;
    value: number;
    type: string;
}

interface clicksDistribution {
    country: string;
    numberOfClicks: number;
}

const FONT = 'Poppins';

const ClicksAndBudgetGraph = (
    {budgetUtilizationPerCampaign, clicksPerCampaign, clicksGeographicalDistribution}: EarningsAndCLicksProps) => {

    clicksPerCampaign = clicksPerCampaign.map(({numberOfClicks, ...rest}: any) => ({
        value: numberOfClicks,
        type: 'Clicks',
        ...rest
    }));

    budgetUtilizationPerCampaign = budgetUtilizationPerCampaign.map(({utilization, ...rest}: any) => ({
        value: utilization,
        type: 'Utilization',
        ...rest
    }));

    const data: DataItem[] = budgetUtilizationPerCampaign.concat(clicksPerCampaign)
    const data1: clicksDistribution[] = clicksGeographicalDistribution
        .map(({numberOfClicks, country, ...rest}: any) => ({
            'Clicks': numberOfClicks,
            'Country': country,
            ...rest
        }));

    const config = {
        data: data || [],
        xField: 'campaign_name',
        yField: 'value',
        colorField: 'type',
        isGroup: true,
        autoFit: true,
        height: 320,
        responsive: true,
        style: {inset: 5},
        axis: {
            x: {
                labelFontFamily: FONT,
                labelAutoHide: true,
                labelAutoRotate: true,
                labelRotate: -0.6,
                formatter: (v: string) => truncateStr(v, 12)
            },
            y: {labelFontFamily: FONT}
        },
        tooltip: {
            title: {style: {fontFamily: FONT}},
            itemName: {style: {fontFamily: FONT}},
            itemValue: {style: {fontFamily: FONT}}
        }
    };

    const configClicksForCountry = {
        data: data1 || [],
        xField: 'Country',
        yField: 'Clicks',
        point: {shapeField: 'square', sizeField: 4},
        style: {lineWidth: 2},
        responsive: true,
        height: 320,
        autoFit: true,
        axis: {
            x: {
                labelFontFamily: FONT,
                labelAutoHide: true,
                labelAutoRotate: true,
                formatter: (v: string) => truncateStr(v, 10)
            },
            y: {labelFontFamily: FONT},
        },
        tooltip: {
            title: {style: {fontFamily: FONT}},
            itemName: {style: {fontFamily: FONT}},
            itemValue: {style: {fontFamily: FONT}}
        }
    };

    const ClicksPerCountryCustomizedCardTitle = () => (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span>Geographical distribution of clicks</span>
            <Tooltip title="Number of clicks gained categorized by user country">
                <InfoCircleOutlined/>
            </Tooltip>
        </div>
    );

    const BudgetUtilizationCardTitle = () => (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span>Budget utilization and number of clicks per campaign</span>
            <Tooltip title="Number of clicks and percentage of budget used for each campaign">
                <InfoCircleOutlined/>
            </Tooltip>
        </div>
    );

    return (
        <Card>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={14} md={14} lg={14} xl={14}>
                    <Card title={<BudgetUtilizationCardTitle/>}>
                        {data.length > 0 ? (
                            <Column {...config} />
                        ) : (
                            <NoDataPlaceholder message="No campaign data to display"/>
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={10} md={10} lg={10} xl={10}>
                    <Card title={<ClicksPerCountryCustomizedCardTitle/>}>
                        {data1.length > 0 ? (
                            <Line {...configClicksForCountry} />
                        ) : (
                            <NoDataPlaceholder message="No geographical data to display"/>
                        )}
                    </Card>
                </Col>
            </Row>
        </Card>
    );
};

export default ClicksAndBudgetGraph;